// Third payty and libraries
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import Select from "react-select";
import { Formik, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useLazyQuery, useMutation } from "@apollo/client";
import _ from "lodash";

// constant
import {
  HAL_KEY,
  JNT_KEY,
  LOGISTICS_DATAS,
  MIXAY_KEY,
} from "../../../../constants";
import {
  GET_HAL_BRANCHS,
  GET_JNT_PROVINCES,
  GET_MIXAY_DROP_SHIPS_V2,
  GET_MIXAY_PROVINCES_V2,
} from "../../../../graphql/shop/shopAddress/query";
import {
  CREATE_USER_ADDRESS,
  UPDATE_USER_ADDRESS,
} from "../../../../graphql/userConfirm/userAddress/mutation";
import { ENSURE_USER_CONFIRM_BY_PHONE } from "../../../../graphql/userConfirm/mutation";
import { validateMainLaotianPhoneNumber } from "../../../../helpers/validatePhone";
import { convertLogicticsName } from "../../../../helpers";
import { errorFields, successFields } from "../../../../helpers/sweetalert";
import DeliveryPicker from "../../../orders/OrderConfirm/DeliveryPicker";
import "../../../orders/OrderConfirm/checkout-theme.css";

// react-select ບໍ່ຮັບ className ພາຍໃນ ຈຶ່ງຕ້ອງ map token ຜ່ານ styles prop
// ຄ່າທັງໝົດອີງ --cf-* ໃນ checkout-theme.css ຢ່າ hardcode ຊ້ຳ
const cfSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 46,
    backgroundColor: state.isDisabled
      ? "var(--cf-surface-muted)"
      : "var(--cf-surface)",
    borderColor: state.isFocused ? "var(--cf-primary)" : "var(--cf-border)",
    borderRadius: "var(--cf-radius-input)",
    boxShadow: state.isFocused ? "0 0 0 3px var(--cf-primary-tint)" : "none",
    fontSize: 14,
    ":hover": {
      borderColor: state.isFocused
        ? "var(--cf-primary)"
        : "var(--cf-border-strong)",
    },
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 12px" }),
  singleValue: (base) => ({ ...base, color: "var(--cf-text)" }),
  // #6e7686 ຜ່ານ WCAG AA (4.57:1) ຄ່າ default ຂອງ react-select ຈາງເກີນໄປ
  placeholder: (base) => ({ ...base, color: "#6e7686" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, color: "var(--cf-text-secondary)" }),
  menu: (base) => ({
    ...base,
    borderRadius: "var(--cf-radius-input)",
    overflow: "hidden",
    fontSize: 14,
    zIndex: 20,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "var(--cf-primary)"
      : state.isFocused
        ? "var(--cf-primary-tint)"
        : "transparent",
    color: state.isSelected ? "var(--cf-on-primary)" : "var(--cf-text)",
  }),
};

const toIntOrUndefined = (value) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const _number = Number(value);
  return Number.isFinite(_number) ? _number : undefined;
};

// ຄ່າວ່າງຂອງ formState — ໃຊ້ຮ່ວມກັນລະຫວ່າງຕອນ init ກັບຕອນ reset
// ຢ່າແຍກເປັນ 2 ບ່ອນ ບໍ່ງັ້ນເພີ່ມ key ໃໝ່ແລ້ວລືມ reset
const EMPTY_FORM_STATE = {
  _province: "",
  _district: "",
  _village: "",
  _branch: "",
  _receive_branch_id: "",
  _branch_code: "",
  _postCode: "",
  _address_detail: "",
  _dropship_start: "",
};

// Validation schema using Yup
const validationSchema = Yup.object().shape({
  userName: Yup.string().required("ຕ້ອງປ້ອນຊື່ຜູ້ຝາກ ຫຼື ຊື່ຮ້ານກ່ອນ!"),
  userTel: Yup.string()
    .required("ຕ້ອງປ້ອນເບີໂທລະສັບກ່ອນ!")
    .matches(
      /^\d{8}$/,
      "ເບີໂທ ຕ້ອງເປັນຕົວເລກ 0 - 9 ເທົ່ານັ້ນ ບໍ່ໃສ່ 020 ຫລື 20 ດ້ານຫນ້າ",
    ),
});

export default function FormReceiveAddress({
  editData,
  userConfirm,
  psid,
  onCloseModalAdd,
  fetchUserAddressData,
  orderData,
  selectedDelivery,
  settingByShop,
  onUseManualAddress,
  receiverName,
  receiverPhone,
  saveRef,

  savedAddresses,
}) {
  // ຕ້ອງເຊັກແບບ !== undefined ບໍ່ແມ່ນ truthy — ຊື່ຫວ່າງກໍ່ຍັງນັບວ່າໜ້າແມ່ເປັນຄົນຄຸມ
  const isContactFromParent =
    receiverName !== undefined || receiverPhone !== undefined;
  const isSaveDrivenByParent = !!saveRef;
  const [delivery, setDelivery] = useState({
    name: "",
    img: "",
  });
  const [userConfirmData, setUserConfirmData] = useState("");
  const [isModalError, setIsModalError] = useState({
    error: false,
    message: "",
  });

  const enabledLogisticsOptions = useMemo(() => {
    const isOpenByKey = {
      [HAL_KEY]: settingByShop?.halIsOpen,
      [MIXAY_KEY]: settingByShop?.mxIsOpen,
      [JNT_KEY]: settingByShop?.jntIsOpen,
    };
    const _enabled = logisticsOptions.filter(
      (option) => isOpenByKey[option?.value] !== false,
    );
    //* Never render an empty picker: if a shop has switched everything off, showing the
    //* full list is better than a dead control the customer cannot get past.
    return _enabled.length ? _enabled : logisticsOptions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    settingByShop?.halIsOpen,
    settingByShop?.mxIsOpen,
    settingByShop?.jntIsOpen,
  ]);

  useEffect(() => {
    if (userConfirm) {
      setUserConfirmData(userConfirm);
    }
  }, [userConfirm]);

  useEffect(() => {
    if (!editData?.deliveryName) return;

    const _branch =
      editData?.branch || editData?.village || editData?.address || "";
    setFormState((prev) => ({
      ...prev,
      _province: editData?.province,
      _district: editData?.district,
      _village: _branch,
      _branch: _branch,
      _branch_code: editData?.branchCode || "",
      _receive_branch_id: editData?.receive_branch_id || "",
    }));
    setIsDelivery({
      delivery: true,
      province: !_.isEmpty(editData?.province),
      district: !_.isEmpty(editData?.district),
      // ที่อยู่เก่าที่บันทึกไว้ก่อนมีระบบสาขา จะไม่มี branchCode
      // ยอมให้ผ่านถ้ามีชื่อสาขา/ที่อยู่เดิม ไม่งั้นแก้ไขที่อยู่เก่าไม่ได้เลย
      branch: !_.isEmpty(_branch.trim()),
    });
  }, [editData?.id, editData?.deliveryName]);

  //! State for validate delivery
  const [isDelivery, setIsDelivery] = useState({
    delivery: false,
    province: false,
    district: false,
    branch: false,
  });

  const [targetAddress, setTargetAddress] = useState(editData);

  const draftsByDeliveryRef = useRef({});

  // state
  const [logisticsAddress, setLogisticsAddress] = useState({
    provinces: [],
    districts: [],
    villages: [],
    branchDatas: [],
  });
  const [provinceOption, setProvinceOption] = useState([]);
  const [districtOption, setDistrictOption] = useState([]);
  const [villageOption, setVillageOption] = useState([]);
  const [formState, setFormState] = useState(EMPTY_FORM_STATE);
  // ຕ້ອງເຂົ້າເຖິງ Formik ຈາກນອກ render prop ເພື່ອລ້າງຄ່າ Select ຕອນສະຫຼັບຂົນສົ່ງ
  const formikRef = useRef(null);
  const prevSelectedDeliveryRef = useRef(selectedDelivery);
  const [activeLogictics, setActiveLogictics] = useState();
  const [mixayDropShipByProvince, setMixayDropShipByProvince] = useState([]);

  const resetAddressFields = (setFieldValue) => {
    setFormState(EMPTY_FORM_STATE);
    setProvinceOption([]);
    setDistrictOption([]);
    setVillageOption([]);
    setMixayDropShipByProvince([]);

    const _setValue = setFieldValue || formikRef.current?.setFieldValue;
    if (_setValue) {
      _setValue("province", null);
      _setValue("district", null);
      _setValue("village", null);
    }
  };

  useEffect(() => {
    if (!isContactFromParent) return;
    const _setValue = formikRef.current?.setFieldValue;
    if (!_setValue) return;

    _setValue("userName", receiverName ?? "");
    _setValue("userTel", receiverPhone ?? "");
  }, [isContactFromParent, receiverName, receiverPhone]);

  useEffect(() => {
    if (prevSelectedDeliveryRef.current === selectedDelivery) return;
    prevSelectedDeliveryRef.current = selectedDelivery;

    resetAddressFields();
    setIsDelivery({
      delivery: !_.isEmpty(selectedDelivery),
      province: false,
      district: false,
      branch: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDelivery]);

  // apollo client query
  const [getHalBranchDatas, { data: halBranchDatas }] = useLazyQuery(
    GET_HAL_BRANCHS,
    { fetchPolicy: "network-only" },
  );
  // const [getMixayProvinces, { data: mixayProvinces, loading: loadingProvinceMixay }] = useLazyQuery(GET_MIXAY_PROVINCES, { fetchPolicy: 'network-only' });
  const [getMixayProvincesV2, { data: dataMixayProvincesV2 }] = useLazyQuery(
    GET_MIXAY_PROVINCES_V2,
    { fetchPolicy: "network-only" },
  );
  // const [getMixayDropShips, { data: mixayDropShips, loading: loadingDropshipMixay }] = useLazyQuery(GET_MIXAY_DROPSHIPS, { fetchPolicy: 'network-only' });
  const [getMixayDropShipsV2] = useLazyQuery(GET_MIXAY_DROP_SHIPS_V2, {
    fetchPolicy: "network-only",
  });
  //TODO JNT — โครงเหมือน HAL (districts มี province_id) ใช้ logic กรองเดียวกัน
  const [getJntProvinces, { data: jntProvinces }] = useLazyQuery(
    GET_JNT_PROVINCES,
    { fetchPolicy: "cache-first" },
  );

  //TODO Edit data
  const handleDefaultLogistic = (value) => {
    setActiveLogictics(value);
    switch (value) {
      case HAL_KEY:
        return {
          value: LOGISTICS_DATAS?.hal?.keyDefault,
          label: (
            <div>
              <img
                style={{
                  width: 20,
                  height: 20,
                  marginRight: 10,
                  borderRadius: "40em",
                }}
                src={LOGISTICS_DATAS?.hal?.image}
                alt={LOGISTICS_DATAS?.hal?.keyDefault}
              />
              {LOGISTICS_DATAS?.hal?.name}
            </div>
          ),
        };
      case MIXAY_KEY:
        return {
          value: LOGISTICS_DATAS?.mixay?.keyDefault,
          label: (
            <div>
              <img
                style={{
                  width: 20,
                  height: 20,
                  marginRight: 10,
                  borderRadius: 5,
                }}
                src={LOGISTICS_DATAS?.mixay?.image}
                alt={LOGISTICS_DATAS?.mixay?.keyDefault}
              />
              {LOGISTICS_DATAS?.mixay?.name}
            </div>
          ),
        };
      case JNT_KEY:
        return {
          value: LOGISTICS_DATAS?.jnt?.keyDefault,
          label: (
            <div>
              <img
                style={{
                  width: 20,
                  height: 20,
                  marginRight: 10,
                  borderRadius: "40em",
                }}
                src={LOGISTICS_DATAS?.jnt?.image}
                alt={LOGISTICS_DATAS?.jnt?.keyDefault}
              />
              {LOGISTICS_DATAS?.jnt?.name}
            </div>
          ),
        };
      default:
        return null;
    }
  };
  // useMemo
  const defaultDelivery = useMemo(() => {
    // ลูกค้าเลือกขนส่งจาก dropdown หน้ายืนยันออเดอร์มาแล้ว ล็อกไว้เลย ไม่ต้องให้เลือกซ้ำ
    const name = editData?.deliveryName || selectedDelivery;
    if (name) {
      setIsDelivery({ ...isDelivery, delivery: true });
      if (name === HAL_KEY) {
        getHalBranchDatas();
      } else if (name === MIXAY_KEY) {
        getMixayProvincesV2();
      } else if (name === JNT_KEY) {
        getJntProvinces();
      }
      setDelivery({
        name: name,
        img: editData?.img || LOGISTICS_DATAS?.[name.toLowerCase()]?.image,
      });
      return handleDefaultLogistic(name);
    } else return null;
  }, [editData?.deliveryName, selectedDelivery]);

  //TODO HAL
  useEffect(() => {
    if (activeLogictics !== HAL_KEY || !halBranchDatas) return;

    // Set logistics address from the response data
    setLogisticsAddress({
      provinces: halBranchDatas?.getHalBranchList?.provinces,
      districts: halBranchDatas?.getHalBranchList?.districts,
      villages: halBranchDatas?.getHalBranchList?.villages,
      branchDatas: JSON.parse(halBranchDatas?.getHalBranchList?.branchDatas),
    });

    // Map province options for react-select
    const provinceOptions = halBranchDatas?.getHalBranchList?.provinces.map(
      (province) => ({
        value: province.id,
        label: province.name,
      }),
    );
    setProvinceOption(provinceOptions);

    // Map village options for react-select
    const villageOptions = halBranchDatas?.getHalBranchList?.villages.map(
      (village) => ({
        value: village.id,
        label: village.name,
      }),
    );

    setVillageOption(villageOptions);
  }, [halBranchDatas, activeLogictics]);

  //TODO MIXAY
  useEffect(() => {
    if (activeLogictics !== MIXAY_KEY || _.isEmpty(dataMixayProvincesV2))
      return;

    let mixayProvinceOptions =
      dataMixayProvincesV2?.getMixayProvincesV2?.provinces.map((province) => ({
        value: province.id,
        label: province.name,
      }));
    setProvinceOption(mixayProvinceOptions);
  }, [dataMixayProvincesV2, activeLogictics]);

  //TODO JNT
  useEffect(() => {
    if (activeLogictics !== JNT_KEY || _.isEmpty(jntProvinces)) return;

    setLogisticsAddress((prev) => ({
      ...prev,
      provinces: jntProvinces?.getJntProvinces?.provinces,
      districts: jntProvinces?.getJntProvinces?.districts,
      branchDatas: JSON.parse(
        jntProvinces?.getJntProvinces?.branchDatas || "[]",
      ),
    }));
    const jntProvinceOptions = jntProvinces?.getJntProvinces?.provinces.map(
      (province) => ({
        value: province?.id,
        label: province?.name,
      }),
    );
    setProvinceOption(jntProvinceOptions);
  }, [jntProvinces, activeLogictics]);

  // apollo client mutation
  const [addUserAddressAPI, { loading: addUserAddressLoading }] =
    useMutation(CREATE_USER_ADDRESS);
  const [putUserAddressAPI, { loading: putUserAddressLoading }] =
    useMutation(UPDATE_USER_ADDRESS);
  const [ensureUserConfirmByPhone_API] = useMutation(
    ENSURE_USER_CONFIRM_BY_PHONE,
  );

  //TODO create user address
  const handleProcessAPI = async (values, { silent } = {}) => {
    try {
      if (addUserAddressLoading || putUserAddressLoading) return;
      if (!isDelivery.branch) {
        // handleAlert("ກະລຸນາເລືອກຂົນສົ່ງ-ສາຂາ!", "warning");
        setIsModalError((prev) => ({
          ...prev,
          error: true,
          message: "ກະລຸນາເລືອກຂົນສົ່ງ-ສາຂາ!",
        }));
        return { isError: true };
      }

      // validate phone
      if (!validateMainLaotianPhoneNumber(values?.userTel)) {
        // handleAlert("ກະລຸນາປ້ອນເບີໂທໃຫ້ຖືກ!", "warning");
        setIsModalError((prev) => ({
          ...prev,
          error: true,
          message: "ກະລຸນາປ້ອນເບີໂທໃຫ້ຖືກ!",
        }));
        return { isError: true };
      }

      let _userId = userConfirmData?.id;
      if (!_userId) {
        const res = await ensureUserConfirmByPhone_API({
          variables: { phone: values?.userTel },
        });
        _userId = res?.data?.ensureUserConfirmByPhone?.id;
      }
      if (!_userId) throw "USER_ID_IS_REQUIRED";

      // Receive Or User Address
      let dataRequest = {
        ...values,
        user: _userId,
        deliveryName: delivery?.name,
        img: delivery?.img,
        province: formState._province,
        district: formState?._district,
        village: formState?._village,
        branch: formState?._branch,
        branchCode: formState?._branch_code,
        receive_branch_id: toIntOrUndefined(formState?._receive_branch_id),
        labelAs: "Delivery",
        address: `${formState?._branch_code}-${formState?._branch}(${formState?._village}-${formState?._district}-${formState?._province})`,
        postcode: formState?._postCode,
      };
      // remove column logistics in dataRequest
      delete dataRequest.logistics;
      if (delivery?.name === JNT_KEY) {
        const _jntBranchName = (formState?._branch || "").trim();
        const _jntBranchCode = (formState?._branch_code || "").trim();

        if (_.isEmpty(_jntBranchCode)) {
          setIsModalError((prev) => ({
            ...prev,
            error: true,
            message: "ກະລຸນາເລືອກສາຂາ!",
          }));
          return { isError: true };
        }

        dataRequest = {
          ...dataRequest,
          village: _jntBranchName,
          branch: _jntBranchName,
          address: _jntBranchName,
          branchCode: _jntBranchCode,
        };
        delete dataRequest.receive_branch_id;
      } else if (delivery?.name === MIXAY_KEY) {
        dataRequest = {
          ...dataRequest,
          address: formState?._address_detail
            ? formState?._address_detail
            : formState?._branch_code +
              "-" +
              formState?._village +
              "-" +
              formState?._district +
              "-" +
              formState?._province,
          postcode: formState?._postCode,
          // dropship_start ເປັນ Int — ຄ່າຕັ້ງຕົ້ນເປັນ "" ຈະຕີກັບຖ້າສົ່ງໄປຊື່ໆ
          // (ເກີດຕອນເປີດຟອມມາພ້ອມທີ່ຢູ່ເກົ່າແລ້ວແກ້ແຕ່ສາຂາ ໂດຍບໍ່ຜ່ານ onChangeBranch)
          dropship_start: toIntOrUndefined(formState?._dropship_start),
        };
        if (_.isEmpty(dataRequest?.address)) {
          setIsModalError((prev) => ({
            ...prev,
            error: true,
            message: "ບໍ່ສາສມາດສ້າງຂົນສົ່ງມີໄຊ, ກະລຸນາລອງໃຫມ່ພາຍຫລັງ!",
          }));
          return { isError: true };
        }
        delete dataRequest.receive_branch_id;
      } else {
        if (dataRequest?.receive_branch_id < 0) {
          setIsModalError((prev) => ({
            ...prev,
            error: true,
            message: "ບໍ່ສາສມາດສ້າງຂົນສົ່ງຮຸ່ງອາລຸນ, ກະລຸນາລອງໃຫມ່ພາຍຫລັງ!",
          }));
          return { isError: true };
        }
      }

      const isSameDelivery = targetAddress?.deliveryName === delivery?.name;
      const isEditingExisting = !!targetAddress?.id && isSameDelivery;

      let _savedId = targetAddress?.id;
      if (isEditingExisting) {
        await putUserAddressAPI({
          variables: {
            where: {
              id: targetAddress?.id,
              deliveryName: delivery?.name,
              user: userConfirmData?.id,
            },
            data: dataRequest,
          },
        });
        // ໜ້າແມ່ສັ່ງບັນທຶກເອງ (ຕອນກົດຢືນຢັນຊຳລະ) — ບໍ່ຄວນເດັ້ງ popup ຄັ່ນກາງທາງ
        if (!silent) {
          successFields({ text: "ແກ້ໄຂທີ່ຢູ່ຈັດສົ່ງສຳເລັດ" });
        }
      } else {
        const _res = await addUserAddressAPI({
          variables: {
            data: dataRequest,
          },
        });
        // put ຄືນມາແຕ່ message — create ຄືນ id ມາໃຫ້ ໃຊ້ຫາໃບທີ່ຫາກໍ່ສ້າງ
        _savedId = _res?.data?.addUserAddress?.id || _savedId;
        if (!silent) {
          successFields({ text: "ເພີ່ມທີ່ຢູ່ຂົນສົ່ງສຳເລັດ" });
        }
      }

      const _savedRecord = {
        ...dataRequest,
        id: _savedId,
        latLong: targetAddress?.latLong,
      };
      delete draftsByDeliveryRef.current[delivery?.name];
      setTargetAddress(_savedRecord);

      if (silent) {
        fetchUserAddressData(values?.userTel);
        // ບໍ່ລ້າງທຸງ — ຟອມຍັງຄາໜ້າຢູ່ ຈະຂຶ້ນສີແດງທັງທີ່ບັນທຶກສຳເລັດ
        onCloseModalAdd();
        return { isError: false, data: _savedRecord };
      }

      const _list = await fetchUserAddressData(values?.userTel);
      if (!isSaveDrivenByParent) handleUseEffect();
      onCloseModalAdd();

      const _saved = Array.isArray(_list)
        ? _list.find((item) => item?.id === _savedId) || _list[0]
        : null;
      return { isError: false, data: _saved };
    } catch (error) {
      // handleAlert("ຂໍອະໄພ ບໍ່ສາມາດ ສ້າງໃຊ້ທີ່ຢູ່ສຳລັບການຈັດສົ່ງ ກະລຸນາລອງໃຫມ່ພາຍຫລັງ", "error");
      console.log("ERROR_handleProcessAPI: ", error);
      if (error?.message === "Error: DUPLICATE_ADDRESS") {
        setIsModalError((prev) => ({
          ...prev,
          error: true,
          message:
            "ຂໍອະໄພ ບໍ່ສາມາດ ສ້າງຫລືແກ້ໄຂທີ່ຢູ່ສຳລັບການຈັດສົ່ງທີ່ຊຳກັນ ກະລຸນາລອງໃຫມ່ພາຍຫລັງ",
        }));
      } else {
        setIsModalError((prev) => ({
          ...prev,
          error: true,
          message:
            "ຂໍອະໄພ ບໍ່ສາມາດ ສ້າງຫລືແກ້ໄຂທີ່ຢູ່ສຳລັບການຈັດສົ່ງ ກະລຸນາລອງໃຫມ່ພາຍຫລັງ",
        }));
      }
      return { isError: true };
    }
  };

  /**
   * ໃຫ້ໜ້າແມ່ສັ່ງບັນທຶກໄດ້ຈາກປຸ່ມຢືນຢັນຊຳລະ
   *
   * ຕ້ອງ validate ດ້ວຍ Yup ເອງກ່ອນ ເພາະບໍ່ໄດ້ຜ່ານ onSubmit ຂອງ Formik
   * ແລະ ຕ້ອງ setTouched ນຳ ບໍ່ດັ່ງນັ້ນ <ErrorMessage> ຈະບໍ່ໂຜ່ ລູກຄ້າຈະບໍ່ຮູ້ວ່າຜິດຢູ່ໃສ
   */
  useEffect(() => {
    if (!saveRef) return;

    saveRef.current = async () => {
      const _formik = formikRef.current;
      if (!_formik) return { isError: true };

      const _errors = await _formik.validateForm();
      if (Object.keys(_errors).length > 0) {
        _formik.setTouched(
          Object.keys(_errors).reduce(
            (acc, key) => ({ ...acc, [key]: true }),
            {},
          ),
        );
        return { isError: true };
      }

      return handleProcessAPI(_formik.values, { silent: true });
    };
  });

  const handleAlert = (message, appearance) => {
    if (appearance === "success") successFields({ text: message });
    else errorFields({ text: message });
  };

  // get mixay dropships
  const handleMixayDropShip = async (id) => {
    try {
      const response = await getMixayDropShipsV2({
        variables: {
          where: {
            provinceId: id,
          },
        },
      });
      if (!_.isEmpty(response?.data)) {
        const _resDropShip = JSON.parse(
          response?.data?.getMixayDropShipsV2?.branchDatas,
        );

        // Create a Set to store unique district names
        const uniqueDistricts = new Set();

        // Filter the districts and map them to the options format, ensuring each district name is unique
        const provinceOptions = _resDropShip
          .filter((district) => {
            const isUnique = !uniqueDistricts.has(district?.dristric?.dr_id);
            uniqueDistricts.add(district?.dristric?.dr_id); // Add the district name to the set
            return isUnique;
          })
          .map((district) => {
            return {
              value: district?.dristric?.dr_id, //district.id,
              label: district?.dristric?.dr_name, //district.dristric,
            };
          });

        // Set the district options
        // setIsDelivery({ ...isDelivery, province: true });
        setDistrictOption(provinceOptions);
        setMixayDropShipByProvince(_resDropShip);
      }
    } catch (error) {
      console.log("Error in handleMixayDropShip:", error);
      handleAlert("ພົບຂໍ້ຜີດຜາດ ບໍ່ພົບຂໍມູນສາຂາ", "error");
      return;
    }
  };

  // handle change get logistics
  const handleChange = (selectedOption, setFieldValue) => {
    setFieldValue("logistics", selectedOption);
    setActiveLogictics(selectedOption?.value);

    const _prevKey = activeLogictics;
    if (_prevKey && _prevKey !== selectedOption?.value) {
      const _prevValues = formikRef.current?.values;
      const _prevSaved = (savedAddresses || []).find(
        (item) => item?.deliveryName === _prevKey,
      );
      const _isPrevDirty =
        (_prevValues?.province?.label ?? null) !==
          (_prevSaved?.province ?? null) ||
        (_prevValues?.district?.label ?? null) !==
          (_prevSaved?.district ?? null) ||
        (_prevValues?.village?.label ?? null) !==
          (_prevSaved?.branch ?? _prevSaved?.village ?? null);

      if (_isPrevDirty) {
        draftsByDeliveryRef.current[_prevKey] = {
          formState,
          isDelivery,
          province: _prevValues?.province ?? null,
          district: _prevValues?.district ?? null,
          village: _prevValues?.village ?? null,
        };
      } else {
        delete draftsByDeliveryRef.current[_prevKey];
      }
    }

    resetAddressFields(setFieldValue);

    const _saved = (savedAddresses || []).find(
      (item) => item?.deliveryName === selectedOption?.value,
    );
    setTargetAddress(_saved || null);

    const _draft = draftsByDeliveryRef.current[selectedOption?.value];

    if (_draft) {
      setFormState(_draft.formState);
      setFieldValue("province", _draft.province);
      setFieldValue("district", _draft.district);
      setFieldValue("village", _draft.village);
      setIsDelivery({ ..._draft.isDelivery, delivery: true });
    } else if (_saved) {
      const _branch = _saved?.branch || _saved?.village || "";
      setFormState((prev) => ({
        ...prev,
        _province: _saved?.province,
        _district: _saved?.district,
        _village: _branch,
        _branch: _branch,
        _branch_code: _saved?.branchCode || "",
        _receive_branch_id: _saved?.receive_branch_id || "",
      }));
      setFieldValue("province", { value: 0, label: _saved?.province });
      setFieldValue("district", { value: 0, label: _saved?.district });
      setFieldValue("village", { value: 0, label: _branch });
      setIsDelivery({
        delivery: true,
        province: !_.isEmpty(_saved?.province),
        district: !_.isEmpty(_saved?.district),
        branch: !_.isEmpty(_branch.trim()),
      });
    } else {
      setIsDelivery({
        ...isDelivery,
        delivery: true,
        province: false,
        district: false,
        branch: false,
      });
    }
    if (selectedOption?.value === HAL_KEY) {
      setDelivery({ name: "HAL", img: selectedOption?.img });
      getHalBranchDatas();
      // setIsDelivery({ ...isDelivery, delivery: true });
    } else if (selectedOption?.value === MIXAY_KEY) {
      setDelivery({ name: "MIXAY", img: selectedOption?.img });
      // setIsDelivery({ ...isDelivery, delivery: true, province: !isDelivery?.province, district: !isDelivery?.district, branch: !isDelivery?.branch });
      // getMixayProvinces()
      getMixayProvincesV2();
    } else if (selectedOption?.value === JNT_KEY) {
      setDelivery({
        name: "JNT",
        img: selectedOption?.img || LOGISTICS_DATAS?.jnt?.image,
      });
      getJntProvinces();
    } else {
      setProvinceOption();
      setDistrictOption();
      setVillageOption();
    }
  };

  // select province to filter district alls
  const onChangeProvince = (value, setFieldValue) => {
    setFieldValue("province", value);
    setFormState({ ...formState, _province: value?.label });
    setIsDelivery({
      ...isDelivery,
      province: true,
      district: false,
      branch: false,
    });

    switch (activeLogictics) {
      case MIXAY_KEY:
        // Handle MIXAY_KEY logic
        handleMixayDropShip(value?.value);
        break;

      case HAL_KEY:
      case JNT_KEY:
        // HAL กับ JNT ใช้โครงเดียวกัน — กรอง districts ด้วย province_id
        const _districtOptions = logisticsAddress?.districts.filter(
          (data) => data?.province_id === value?.value,
        );

        const provinceOptions = _districtOptions.map((district) => ({
          value: district.id,
          label: district.name,
        }));
        setDistrictOption(provinceOptions);
        break;

      default:
        // Handle any other keys or default logic if needed
        break;
    }
  };

  // select district to filter branch alls
  const onChangeDistrict = async (value, setFieldValue) => {
    setFieldValue("district", value);
    if (!logisticsAddress?.branchDatas || !value) return;

    setFormState({ ...formState, _district: value?.label });
    setIsDelivery({ ...isDelivery, district: true, branch: false });

    switch (activeLogictics) {
      case MIXAY_KEY:
        // Handle MIXAY_KEY logic
        handleMixayDistrict(value?.value);
        break;

      case JNT_KEY: {
        // ⚠️ district_id ของ J&T เป็น cityCode ("0101") ซึ่งเป็น string
        // ห้ามใช้ Number() เทียบแบบ HAL — "0101" จะกลายเป็น 101 แล้วไม่ตรงกับอะไรเลย
        const _jntBranches = (logisticsAddress.branchDatas || []).filter(
          (data) => String(data?.district_id) === String(value?.value),
        );
        setVillageOption(
          _jntBranches.map((branch) => ({
            value: branch?.id,
            label: branch?.name,
          })),
        );
        // สลับเมืองแล้วต้องล้างสาขาของเมืองเก่าทิ้ง ไม่งั้นค้างข้ามเมือง
        setFormState((prev) => ({
          ...prev,
          _branch_code: "",
          _branch: "",
          _village: "",
        }));
        break;
      }

      case HAL_KEY:
        // Handle HAL_KEY logic
        const _villageOptions = logisticsAddress.branchDatas.filter((data) => {
          return Number(data?.district_id) === Number(value?.value);
        });

        const _branchOption = _villageOptions.map((branch) => ({
          value: branch?.id,
          label: branch?.name,
          code: branch?.code,
        }));

        setVillageOption(_branchOption);
        break;

      default:
        // Handle any other keys or default logic if needed
        break;
    }
  };

  // select branch data
  const onChangeBranch = (value, setFieldValue) => {
    setFieldValue("village", value);
    if (activeLogictics === MIXAY_KEY) {
      const _newBranch = mixayDropShipByProvince.filter(
        (b) => b?.code === value?.value,
      );
      setFormState({
        ...formState,
        _village: _newBranch[0]?.village?.vill_name,
        _province: _newBranch[0]?.province?.pr_name,
        _district: _newBranch[0]?.dristric?.dr_name,
        _branch: _newBranch[0]?.name,
        _branch_code: _newBranch[0]?.code,
        _dropship_start: _newBranch[0]?.id,
        _postCode: _newBranch[0]?.postcode,
        _address_detail: _newBranch[0]?.address_detail,
      });
      setIsDelivery({ ...isDelivery, branch: true });
      return;
    }

    if (activeLogictics === JNT_KEY) {
      // J&T เก็บรหัสสาขา 6 หลักไว้ใน value (ไม่มี field code แบบ HAL)
      // และไม่มี receive_branch_id ที่เป็นตัวเลข จึงห้ามเซ็ต ไม่งั้น "010101" จะไปลงช่องผิด
      setFormState((prev) => ({
        ...prev,
        _village: value?.label,
        _branch: value?.label,
        _branch_code: value?.value,
      }));
      setIsDelivery((prev) => ({ ...prev, branch: true }));
      return;
    }

    setFormState({
      ...formState,
      _village: value?.label,
      _branch: value?.label,
      _receive_branch_id: value?.value,
      _branch_code: value?.code,
    });
    setIsDelivery({ ...isDelivery, branch: true });
  };

  // filter branch in province for mixay
  const handleMixayDistrict = (_keyFilter) => {
    const _dropShipOptions = mixayDropShipByProvince.filter((data) => {
      return data?.dristric?.dr_id === _keyFilter;
    });
    if (!_.isEmpty(_dropShipOptions)) {
      const _branchOption = _dropShipOptions.reduce((options, branch) => {
        options.push({
          value: branch?.code,
          label: branch?.name,
        });
        return options;
      }, []);
      setVillageOption(_branchOption);
    }
  };

  useEffect(() => {
    if (!formState?._province || !provinceOption?.length) return;
    if (districtOption?.length) return;

    const _prov = provinceOption.find(
      (item) => item?.label === formState._province,
    );
    if (!_prov) return;

    if (activeLogictics === MIXAY_KEY) {
      handleMixayDropShip(_prov?.value);
      return;
    }

    const _districts = (logisticsAddress?.districts || []).filter(
      (item) => item?.province_id === _prov?.value,
    );
    setDistrictOption(
      _districts.map((item) => ({ value: item?.id, label: item?.name })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    provinceOption,
    districtOption,
    logisticsAddress,
    activeLogictics,
    formState?._province,
  ]);

  // ຕໍ່ຈາກຂ້າງເທິງ — ມີລາຍການເມືອງແລ້ວ ຄ່ອຍສ້າງລາຍການສາຂາຂອງເມືອງນັ້ນ
  useEffect(() => {
    if (!formState?._district || !districtOption?.length) return;
    if (villageOption?.length) return;

    const _dist = districtOption.find(
      (item) => item?.label === formState._district,
    );
    if (!_dist) return;

    if (activeLogictics === MIXAY_KEY) {
      handleMixayDistrict(_dist?.value);
      return;
    }

    if (activeLogictics === JNT_KEY) {
      // district_id ຂອງ J&T ເປັນ cityCode ("0101") ເປັນ string — ຫ້າມໃຊ້ Number()
      const _branches = (logisticsAddress?.branchDatas || []).filter(
        (item) => String(item?.district_id) === String(_dist?.value),
      );
      setVillageOption(
        _branches.map((item) => ({ value: item?.id, label: item?.name })),
      );
    }
  }, [
    districtOption,
    villageOption,
    logisticsAddress,
    activeLogictics,
    formState?._district,
    mixayDropShipByProvince,
  ]);

  function handleUseEffect() {
    setIsDelivery({
      delivery: false,
      province: false,
      district: false,
      branch: false,
    });
  }

  return (
    <>
      <Formik
        innerRef={formikRef}
        initialValues={{
          userName:
            editData?.userName ??
            orderData?.cfName ??
            orderData?.customerName ??
            "",
          userTel: editData?.userTel ?? userConfirmData?.phone ?? "",
          codNumber: editData?.codNumber ?? "",
          logistics: defaultDelivery,
          province: { value: 0, label: editData?.province } ?? null,
          district: { value: 0, label: editData?.district } ?? null,
          village:
            {
              value: 0,
              label: editData?.branch ?? editData?.village ?? null,
            } ?? null,
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          handleProcessAPI(values);
        }}
      >
        {({ handleSubmit, setFieldValue, values }) => (
          <Form noValidate onSubmit={handleSubmit} className="cf-form">
            <div className="cf-form__title cf-form__title--alert">
              ***** ກະລຸນາສ້າງທີ່ຢູ່ສຳລັບຂົນສົ່ງຂອງທ່ານ!. *****
            </div>

            <div className="cf-field">
              <label className="cf-field__label">
                ບໍລິການຂົນສົ່ງ (Delivery Service)
              </label>
              <DeliveryPicker
                value={values.logistics?.value || ""}
                onChange={(key) => {
                  if (key === MANUAL_ADDRESS_KEY) {
                    onUseManualAddress();
                    return;
                  }
                  const _option = enabledLogisticsOptions.find(
                    (item) => item?.value === key,
                  );
                  if (_option) handleChange(_option, setFieldValue);
                }}
                options={[
                  //* ບໍ່ປິດເຈົ້າໃດແລ້ວ — ຂົນສົ່ງທີ່ຮ້ານເປີດໄວ້ ເລືອກໄດ້ໝົດ
                  //* (ການກອງວ່າຮ້ານເປີດເຈົ້າໃດແດ່ ຢູ່ໃນ enabledLogisticsOptions)
                  ...enabledLogisticsOptions.map((item) => ({
                    value: item?.value,
                    label: convertLogicticsName(item?.value),
                  })),
                  ...(onUseManualAddress
                    ? [{ value: MANUAL_ADDRESS_KEY, label: "ພິມທີ່ຢູ່ເອງ" }]
                    : []),
                ]}
              />
              {isDelivery?.delivery || editData?.deliveryName ? null : (
                <div className="cf-field__error">ກະລຸນາເລືອກຂົນສົ່ງ</div>
              )}
            </div>

            <div className="cf-field">
              <label className="cf-field__label">ແຂວງ (Province)</label>
              <Select
                options={provinceOption}
                onChange={(value) => onChangeProvince(value, setFieldValue)}
                placeholder="ເລືອກ..."
                value={values.province}
                isDisabled={!isDelivery?.delivery ? true : false}
                styles={cfSelectStyles}
              />
              {!isDelivery?.province ? (
                <div className="cf-field__error">ກະລຸນາເລືອກຂົນສົ່ງກ່ອນ</div>
              ) : null}
            </div>

            <div className="cf-field">
              <label className="cf-field__label">ເມືອງ (City)</label>
              <Select
                options={districtOption}
                onChange={(value) => onChangeDistrict(value, setFieldValue)}
                placeholder="ເລືອກ..."
                value={values?.district}
                isDisabled={!isDelivery?.province ? true : false}
                styles={cfSelectStyles}
              />
              {!isDelivery?.district ? (
                <div className="cf-field__error">ກະລຸນາເລືອກແຂວງກ່ອນ</div>
              ) : null}
            </div>

            {/* ທຸກເຈົ້າໃຊ້ dropdown ສາຂາອັນດຽວກັນ ລວມທັງ J&T
                (ເມື່ອກ່ອນ J&T ແຍກເປັນຊ່ອງພິມທີ່ຢູ່ ເພາະຍັງບໍ່ມີລາຍຊື່ສາຂາ) */}
            <div className="cf-field">
              <label className="cf-field__label">ສາຂາ (Branch)</label>
              <Select
                options={villageOption}
                onChange={(value) => onChangeBranch(value, setFieldValue)}
                placeholder="ເລືອກ..."
                value={values.village}
                isDisabled={!isDelivery?.district ? true : false}
                styles={cfSelectStyles}
              />
              {!isDelivery?.district ? (
                <div className="cf-field__error">ກະລຸນາເລືອກເມືອງກ່ອນ</div>
              ) : !isDelivery?.branch ? (
                <div className="cf-field__error">ກະລຸນາເລືອກສາຂາ</div>
              ) : null}
            </div>

            {/* ຊື່/ເບີ ຖາມຢູ່ FindAddressByPhone ດ້ານເທິງແລ້ວ (ໜ້າ checkout)
                ຈຶ່ງເຊື່ອງບ່ອນນີ້ ບໍ່ໃຫ້ຖາມຊ້ຳສອງເທື່ອ
                ໜ້າອື່ນທີ່ບໍ່ໄດ້ສົ່ງ receiverName/receiverPhone ມາ ຍັງເຫັນຄືເກົ່າ */}
            {!isContactFromParent && (
              <>
                <div className="cf-field">
                  <label className="cf-field__label" htmlFor="cf-userName">
                    ຊື່ຜູ້ຮັບ(ຈາກFacebook){" "}
                    <span className="cf-field__required">*</span>
                  </label>
                  <Field
                    id="cf-userName"
                    disabled={orderData?.cfName ? true : false}
                    name="userName"
                    className="cf-input"
                    type="text"
                    maxLength={20}
                    placeholder="ຈຳນວນ 20 ຕົວອັກສອນເທົ່ານັ້ນ"
                  />
                  <ErrorMessage
                    name="userName"
                    component="div"
                    className="cf-field__error"
                  />
                </div>

                <div className="cf-field">
                  <label className="cf-field__label" htmlFor="cf-userTel">
                    ເບີໂທຜູ້ຮັບ <span className="cf-field__required">*</span>
                  </label>
                  <Field
                    id="cf-userTel"
                    name="userTel"
                    className="cf-input"
                    type="tel"
                    inputMode="numeric"
                    maxLength={8}
                    placeholder="ໃສ່ເບີໂທລະສັບ 8 ຕົວເລກ"
                  />
                  <ErrorMessage
                    name="userTel"
                    component="div"
                    className="cf-field__error"
                  />
                </div>
              </>
            )}

            {/* ຊ່ອງຖືກເຊື່ອງແຕ່ Yup ຍັງກວດຢູ່ — ຖ້າບໍ່ໂຊ error ໄວ້
                ລູກຄ້າຈະກົດບັນທຶກແລ້ວບໍ່ມີຫຍັງເກີດຂຶ້ນ ໂດຍບໍ່ຮູ້ວ່າຜິດຢູ່ໃສ
                (ເຊັ່ນ ພິມເບີ 9 ຕົວຢູ່ຊ່ອງດ້ານເທິງ ແຕ່ Yup ບັງຄັບ 8 ຕົວ) */}
            {isContactFromParent && (
              <>
                <ErrorMessage
                  name="userName"
                  component="div"
                  className="cf-field__error"
                />
                <ErrorMessage
                  name="userTel"
                  component="div"
                  className="cf-field__error"
                />
              </>
            )}

            <div className="cf-field">
              <label className="cf-field__label" htmlFor="cf-codNumber">
                ລະຫັດ COD (ຖ້າມີ)
              </label>
              <Field
                id="cf-codNumber"
                name="codNumber"
                className="cf-input"
                type="text"
                inputMode="numeric"
                maxLength={10}
                placeholder="ຈຳນວນ 10 ຕົວອັກສອນເທົ່ານັ້ນ"
              />
              <div className="cf-field__helper">
                ກະລຸນາລະບຸລະຫັດຫາກເປັນການເກັບເງິນປາຍທາງ
              </div>
              <ErrorMessage
                name="codNumber"
                component="div"
                className="cf-field__error"
              />
            </div>

            {/**
             * ໜ້າ checkout: ໂຊປຸ່ມບັນທຶກຕໍ່ເມື່ອລູກຄ້າແກ້ທີ່ຢູ່ຈາກຂອງເດີມແລ້ວ
             *
             * ບໍ່ໄດ້ແກ້ຫຍັງ = ບໍ່ຕ້ອງມີປຸ່ມ ເພາະປຸ່ມ "ຢືນຢັນການຊຳລະ" ບັນທຶກໃຫ້ຢູ່ແລ້ວ
             *
             * ທຽບສະເພາະຊ່ອງທີ່ຢູ່ (ຂົນສົ່ງ/ແຂວງ/ເມືອງ/ສາຂາ) ບໍ່ທຽບຊື່ກັບເບີ
             * ເພາະສອງອັນນັ້ນຖືກ sync ລົງມາຈາກໜ້າແມ່ຕະຫຼອດ ຖ້າເອົາມາທຽບນຳ
             * ປຸ່ມຈະໂຜ່ຕັ້ງແຕ່ລູກຄ້າພິມຊື່ ທັງທີ່ຍັງບໍ່ໄດ້ແຕະທີ່ຢູ່ເລີຍ
             */}
            {isSaveDrivenByParent &&
              (() => {
                const _isAddressChanged =
                  (values?.logistics?.value ?? null) !==
                    (targetAddress?.deliveryName ?? null) ||
                  (values?.province?.label ?? null) !==
                    (targetAddress?.province ?? null) ||
                  (values?.district?.label ?? null) !==
                    (targetAddress?.district ?? null) ||
                  (values?.village?.label ?? null) !==
                    (targetAddress?.branch ?? targetAddress?.village ?? null);

                if (!_isAddressChanged) return null;

                return (
                  <div className="cf-actions">
                    <button
                      type="submit"
                      className="cf-btn cf-btn--primary"
                      disabled={
                        addUserAddressLoading || putUserAddressLoading
                          ? true
                          : false
                      }
                    >
                      {addUserAddressLoading || putUserAddressLoading
                        ? "ກຳລັງບັນທຶກ..."
                        : "ບັນທຶກທີ່ຢູ່"}
                    </button>
                  </div>
                );
              })()}

            {/* ໜ້າອື່ນທີ່ບໍ່ໄດ້ສົ່ງ saveRef ມາ ຍັງມີປຸ່ມ ບັນທຶກ/ຍົກເລີກ ຄືເກົ່າ */}
            {!isSaveDrivenByParent && (
              <div className="cf-actions">
                <button
                  type="submit"
                  className="cf-btn cf-btn--primary"
                  disabled={
                    addUserAddressLoading || putUserAddressLoading
                      ? true
                      : false
                  }
                >
                  {addUserAddressLoading || putUserAddressLoading
                    ? "ກຳລັງບັນທຶກ..."
                    : "ບັນທຶກທີ່ຢູ່ (Save Address)"}
                </button>
                <button
                  disabled={
                    addUserAddressLoading || putUserAddressLoading
                      ? true
                      : false
                  }
                  type="button"
                  className="cf-btn cf-btn--secondary"
                  onClick={() => {
                    onCloseModalAdd();
                    setIsDelivery({
                      delivery: false,
                      province: false,
                      district: false,
                      branch: false,
                    });
                  }}
                >
                  ຍົກເລີກ
                </button>
              </div>
            )}
          </Form>
        )}
      </Formik>
      {/* Modal error */}
      <Modal
        centered
        show={isModalError?.error}
        onHide={() =>
          setIsModalError({ ...isModalError, error: false, message: "" })
        }
        size="lg"
        // Modal portal ອອກນອກ .cf-checkout — ຕິດ class ນີ້ຈຶ່ງໄດ້ font ຊຸດດຽວກັນ
        contentClassName="cf-dialog"
      >
        <Modal.Header closeButton className={"bg-danger text-white"}>
          <Modal.Title>ເພີ່ມທີ່ຢູ່ສຳລັບການຈັດສົ່ງ</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pb-5">
          <p>{isModalError?.message}</p>
        </Modal.Body>
      </Modal>
    </>
  );
}

/*
 * ໝາຍເຫດ: ກ່ອນນີ້ມີ DISABLED_LOGISTICS = [MIXAY_KEY, HAL_KEY] ໄວ້ຕອນທີ່ຍັງແລ່ນ
 * ແຕ່ J&T ຢ່າງດຽວ ຜົນຄືຮ້ານທີ່ເປີດ jntIsOpen ຈະເຫັນ HAL / ມີໄຊ ເປັນສີເທົາ
 * ເລືອກບໍ່ໄດ້ ເຖິງວ່າຮ້ານຈະເປີດ halIsOpen / mxIsOpen ໄວ້ກໍ່ຕາມ.
 * ຕອນນີ້ເອົາອອກແລ້ວ — ໃຫ້ຄ່າ setting ຂອງແຕ່ລະຮ້ານ (enabledLogisticsOptions
 * ຂ້າງເທິງ) ເປັນຄົນຕັດສິນຢ່າງດຽວ.
 */

// ຄ່າພິເສດ ບໍ່ແມ່ນຂົນສົ່ງຈິງ — ໃຊ້ບອກວ່າລູກຄ້າຢາກພິມທີ່ຢູ່ເອງ
// ນຳໜ້າດ້ວຍ __ ຈຶ່ງບໍ່ມີວັນຊ້ຳກັບ keyDefault ຂອງເຈົ້າໃດ
export const MANUAL_ADDRESS_KEY = "__MANUAL_ADDRESS__";

const logisticsOptions = [
  {
    value: LOGISTICS_DATAS?.jnt?.keyDefault,
    label: (
      <div>
        <img
          style={{
            width: 20,
            height: 20,
            marginRight: 10,
            borderRadius: "40em",
          }}
          src={LOGISTICS_DATAS?.jnt?.image}
          alt={LOGISTICS_DATAS?.jnt?.keyDefault}
        />
        {LOGISTICS_DATAS?.jnt?.name}
      </div>
    ),
    img: LOGISTICS_DATAS?.jnt?.image,
  },

  {
    value: LOGISTICS_DATAS?.mixay?.keyDefault,
    label: (
      <div>
        <img
          style={{
            width: 20,
            height: 20,
            marginRight: 10,
            borderRadius: "40em",
          }}
          src={LOGISTICS_DATAS?.mixay?.image}
          alt={LOGISTICS_DATAS?.mixay?.keyDefault}
        />
        {LOGISTICS_DATAS?.mixay?.name}
      </div>
    ),
    img: LOGISTICS_DATAS?.mixay?.image,
  },

  {
    value: LOGISTICS_DATAS?.hal?.keyDefault,
    label: (
      <div>
        <img
          style={{
            width: 20,
            height: 20,
            marginRight: 10,
            borderRadius: "40em",
          }}
          src={LOGISTICS_DATAS?.hal?.image}
          alt={LOGISTICS_DATAS?.hal?.keyDefault}
        />
        {LOGISTICS_DATAS?.hal?.name}
      </div>
    ),
    img: LOGISTICS_DATAS?.hal?.image,
  },

  // {
  //   value: LOGISTICS_DATAS?.anusith?.keyDefault,
  //   label: (
  //     <div>
  //       <img
  //         style={{ width: 20, height: 20, marginRight: 10, borderRadius: '40em' }}
  //         src={LOGISTICS_DATAS?.anusith?.image}
  //         alt={LOGISTICS_DATAS?.anusith?.keyDefault}
  //       />
  //       {LOGISTICS_DATAS?.anusith?.name}
  //     </div>
  //   ),
  //   img: LOGISTICS_DATAS?.anusith?.image,
  // },
];
